import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider
} from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  CheckCircle,
  EventAvailable,
  People
} from "@material-ui/icons";
import EventDialog from "./EventDialog";
import { getEventByEventId, completeEnrollment } from "../../api/event";
import qs from "query-string";
import moment from "moment";
import ReactHtmlParser from "react-html-parser";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    paddingBottom: theme.spacing(8),
    fontFamily: '"Poppins", sans-serif'
  },
  heroBanner: {
    position: "relative",
    width: "100%",
    height: "400px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderRadius: 0,
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      height: "250px"
    }
  },
  heroOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.2) 80%, transparent 100%)",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2)
    }
  },
  logoChip: {
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: theme.spacing(0.5, 1.5),
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  eventTitle: {
    color: "#fff",
    fontWeight: 700,
    fontFamily: '"Poppins", sans-serif',
    marginBottom: theme.spacing(1.5),
    textShadow: "0 2px 8px rgba(0,0,0,0.5)",
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.75rem"
    }
  },
  eventDate: {
    color: "#fff",
    fontFamily: '"Poppins", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "1rem",
    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
    "& svg": {
      fontSize: "1.25rem"
    }
  },
  mainContent: {
    marginTop: theme.spacing(4),
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(3)
    }
  },
  stickyCard: {
    position: "sticky",
    top: theme.spacing(2),
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(94,44,237,0.15)"
  },
  ctaButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: theme.spacing(1.75, 3),
    fontSize: "1.1rem",
    fontWeight: 600,
    borderRadius: 12,
    textTransform: "none",
    boxShadow: "0 4px 16px rgba(102,126,234,0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #5568d3 0%, #6540a0 100%)",
      boxShadow: "0 6px 20px rgba(102,126,234,0.5)",
      transform: "translateY(-2px)"
    },
    "&:disabled": {
      background: "#e0e0e0"
    },
    transition: "all 0.3s ease"
  },
  successAlert: {
    borderRadius: 16,
    padding: theme.spacing(3),
    backgroundColor: "#f0fdf4",
    border: "2px solid #86efac",
    marginBottom: theme.spacing(3)
  },
  infoCard: {
    borderRadius: 16,
    padding: theme.spacing(3),
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: "1px solid #f0f0f0",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(102,126,234,0.15)",
      borderColor: "#667eea"
    }
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  icon: {
    color: "#667eea",
    fontSize: 28
  },
  priceBox: {
    color: "#fff",
    padding: theme.spacing(3),
    borderRadius: 16,
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(236,72,153,0.3)",
    marginBottom: theme.spacing(3)
  },
  memberPrice: {
    backgroundColor: "rgba(255,255,255,0.25)",
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginTop: theme.spacing(1.5),
    backdropFilter: "blur(10px)"
  },
  descriptionCard: {
    borderRadius: 16,
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    border: "1px solid #f0f0f0",
    "& *": {
      fontFamily: '"Poppins", sans-serif'
    }
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60vh",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60vh",
    flexDirection: "column",
    textAlign: "center",
    padding: theme.spacing(3)
  },
  errorImage: {
    maxWidth: "300px",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      maxWidth: "200px"
    }
  },
  backButton: {
    marginTop: theme.spacing(2),
    borderRadius: 10,
    textTransform: "none",
    color: "#666",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: "#f5f5f5"
    }
  },
  posterImage: {
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    borderRadius: 16,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%"
    }
  }
}));

const EventDetails = () => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [event, setEvent] = React.useState({});
  const [isLoading, setLoading] = React.useState(true);
  const [success, setSuccess] = React.useState(false);
  const [enrollmentId, setEnrollmentId] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      const { id, success, enrollment } = qs.parse(window.location.search);
      const result = await getEventByEventId(id || 1);
      setSuccess(success);
      setEvent(result);
      setLoading(false);
      if (success && enrollment) {
        setEnrollmentId(enrollment);
      }
    };
    fetchData();
  }, []);

  React.useEffect(() => {
    const updateData = async () => {
      const result = await completeEnrollment(enrollmentId);
      console.log(result);
    };
    if (enrollmentId) {
      updateData();
    }
  }, [enrollmentId]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={classes.root}>
        <Skeleton variant="rect" height={400} animation="wave" />
        <Container maxWidth="lg" className={classes.mainContent}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Skeleton
                variant="rect"
                height={300}
                style={{ borderRadius: 16 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton
                variant="rect"
                height={400}
                style={{ borderRadius: 16 }}
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    );
  }

  // Error state - event not found or ended
  if (!event || !event.id || event.eventStatus === 2) {
    return (
      <div className={classes.root}>
        <Container maxWidth="md">
          <Box className={classes.errorContainer}>
            <img
              src={require("../../img/utils/fail.png")}
              alt="Event not found"
              className={classes.errorImage}
            />
            <Typography variant="h4" gutterBottom style={{ fontWeight: 600 }}>
              Acara Tidak Dijumpai
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Maaf, acara ini mungkin telah tamat atau tidak wujud lagi.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href="/"
              className={classes.backButton}
              size="large"
            >
              Kembali ke Laman Utama
            </Button>
          </Box>
        </Container>
      </div>
    );
  }

  // Calculate prices
  const fee = Number(event.fee);
  const memberDiscount = Number(event.memberDiscount);
  const memberPrice = fee - (memberDiscount / 100) * fee;

  return (
    <div className={classes.root}>
      {/* Event Dialog */}
      <EventDialog
        id={event.id}
        title={event.name}
        open={open}
        setOpen={setOpen}
        setSucces={setSuccess}
        status={event.eventStatus}
        fee={fee}
        memberDiscount={memberDiscount}
        customFields={event.customFields}
      />

      {/* Hero Banner */}
      <Box
        className={classes.heroBanner}
        style={{
          backgroundImage: event.posterUrl
            ? `url(${event.posterUrl})`
            : `url(${require("../../img/home-event/promo.png")})`
        }}
      >
        <Box className={classes.heroOverlay}>
          <Container maxWidth="lg">
            <Chip
              icon={
                <img
                  src={require("../../img/logo.png")}
                  alt="GEMA"
                  width="24"
                />
              }
              label="GEMA"
              className={classes.logoChip}
            />
            <Typography variant="h3" className={classes.eventTitle}>
              {event.name}
            </Typography>
            <Box className={classes.eventDate}>
              <CalendarToday fontSize="small" />
              <span>
                {moment(event.startDate).format("DD MMM YYYY")} -{" "}
                {moment(event.endDate).format("DD MMM YYYY")}
              </span>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" className={classes.mainContent}>
        <Grid container spacing={4}>
          {/* Left Column - Event Details */}
          <Grid item xs={12} md={8}>
            {/* Success Message */}
            {success && (
              <Alert
                icon={<CheckCircle />}
                severity="success"
                className={classes.successAlert}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ fontWeight: 600 }}
                >
                  Pendaftaran Berjaya!
                </Typography>
                <Typography variant="body2">
                  Terima kasih kerana mendaftar. Anda akan menerima maklumat
                  lanjut tidak lama lagi.
                </Typography>
                {event.registrationNote && (
                  <Box mt={2}>{ReactHtmlParser(event.registrationNote)}</Box>
                )}
              </Alert>
            )}

            {/* Event Description */}
            <Card className={classes.descriptionCard}>
              <Typography
                variant="h5"
                style={{ fontWeight: 700, marginBottom: 24, color: "#2d3748" }}
              >
                Maklumat Program
              </Typography>
              <Typography
                variant="body1"
                style={{
                  lineHeight: 1.8,
                  color: "#4a5568",
                  fontSize: "1rem"
                }}
              >
                {ReactHtmlParser(event.description)}
              </Typography>
            </Card>

            {/* Event Details Grid */}
            <Grid container spacing={3}>
              {/* Venue */}
              <Grid item xs={12} md={6}>
                <Card className={classes.infoCard}>
                  <Box className={classes.iconWrapper}>
                    <LocationOn className={classes.icon} />
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 700, color: "#2d3748" }}
                    >
                      Tempat
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    style={{ color: "#4a5568", lineHeight: 1.6 }}
                  >
                    {event.venue}
                  </Typography>
                </Card>
              </Grid>

              {/* Time */}
              <Grid item xs={12} md={6}>
                <Card className={classes.infoCard}>
                  <Box className={classes.iconWrapper}>
                    <AccessTime className={classes.icon} />
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 700, color: "#2d3748" }}
                    >
                      Masa
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    style={{ color: "#4a5568", lineHeight: 1.6 }}
                  >
                    {moment(event.startDate).format("h:mm A")} -{" "}
                    {moment(event.endDate).format("h:mm A")}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - Registration Card */}
          <Grid item xs={12} md={4}>
            <Card className={classes.stickyCard}>
              <CardContent style={{ padding: "24px" }}>
                {/* Event Poster */}
                {event.posterUrl && (
                  <Box mb={2}>
                    <img
                      src={event.posterUrl}
                      alt={event.name}
                      className={classes.posterImage}
                    />
                  </Box>
                )}

                {/* Price Section */}
                <Box className={classes.priceBox}>
                  <Typography
                    variant="subtitle2"
                    style={{ opacity: 0.95, marginBottom: 8 }}
                  >
                    Yuran Penyertaan
                  </Typography>
                  <Typography
                    variant="h3"
                    style={{
                      fontWeight: 800,
                      fontSize: "2.5rem"
                    }}
                  >
                    {fee === 0 ? "PERCUMA" : `RM${fee.toFixed(2)}`}
                  </Typography>
                  {memberDiscount > 0 && fee > 0 && (
                    <Box className={classes.memberPrice}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Harga Ahli: RM{memberPrice.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" style={{ opacity: 0.9 }}>
                        Jimat {memberDiscount}%
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* CTA Button */}
                {!success && (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      className={classes.ctaButton}
                      onClick={() => setOpen(true)}
                      startIcon={<EventAvailable />}
                    >
                      Daftar Sekarang
                    </Button>
                    <Box textAlign="center">
                      <Button
                        variant="text"
                        href="/"
                        className={classes.backButton}
                      >
                        ← Kembali ke Laman Utama
                      </Button>
                    </Box>
                  </>
                )}

                {/* Event Stats */}
                <Divider style={{ margin: "24px 0" }} />
                <Box>
                  <Typography
                    variant="caption"
                    style={{ color: "#718096", fontWeight: 600 }}
                    display="block"
                    gutterBottom
                  >
                    Status Acara
                  </Typography>
                  <Chip
                    icon={<People />}
                    label={event.eventStatus === 0 ? "Terbuka" : "Penuh"}
                    color={event.eventStatus === 0 ? "primary" : "default"}
                    style={{ marginTop: 4, fontWeight: 600, padding: 10 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default EventDetails;
